import OrchestratorAPI from './common.js';
import {
  type ApiInstance,
  type ApiInstanceBasic,
  type CreateValues,
  type FindAllOptions,
  type FindFhirEndpointsOptions,
  type UpdateValues,
} from '../models/orchestrator/apiInstance.js';
import { type ApiInstanceFhirEndpoint } from '../models/orchestrator/apiInstanceFhir.js';
import { type PaginatedQueryOptions } from '../models/pagination/PaginatedQueryOptions.type.js';
import { type PaginationResponse } from '../models/pagination/PaginationResponse.type.js';

export default class API extends OrchestratorAPI {
  protected readonly API_PATH = '/api-instances';
  public readonly baseEndpoint = `${this.BASE_URL}${this.API_PATH}`;

  async findAllPaginated(
    options: PaginatedQueryOptions<FindAllOptions>
  ): Promise<PaginationResponse<ApiInstance>> {
    const url = this.constructPath('/', options);
    return await this.common.getJSON(url);
  }

  async findAllBasic(options: FindAllOptions = {}): Promise<ApiInstanceBasic[]> {
    const url = this.constructPath('/basic', options);
    const { apiInstances } = await this.common.getJSON(url);
    return apiInstances.map(this.deserialize);
  }

  async findOneById(id: string): Promise<ApiInstance> {
    const url = this.constructPath(`/${id}`);
    const { apiInstance } = await this.common.getJSON(url);
    return this.deserialize(apiInstance);
  }

  async findAll(options: FindAllOptions = {}): Promise<ApiInstance[]> {
    const url = this.constructPath('/', options);
    const { apiInstances } = await this.common.getJSON(url);
    return apiInstances.map(this.deserialize);
  }

  async findOneByIdBasic(id: string): Promise<ApiInstanceBasic> {
    const url = this.constructPath(`/${id}/basic`);
    const { apiInstance } = await this.common.getJSON(url);
    return this.deserialize(apiInstance);
  }

  async findFhirEndpoints(
    id: string,
    queryParams: FindFhirEndpointsOptions
  ): Promise<ApiInstanceFhirEndpoint[]> {
    const url = this.constructPath(`/${id}/fhir-endpoints`, queryParams);
    const { fhirEndpoints } = await this.common.getJSON(url);
    return fhirEndpoints;
  }

  async create(values: CreateValues): Promise<ApiInstance> {
    const url = this.constructPath('/');
    const { apiInstance } = await this.common.postJSON(url, values);
    return this.deserialize(apiInstance);
  }

  async update(id: string, values: UpdateValues): Promise<ApiInstance> {
    const url = this.constructPath(`/${id}`);
    const { apiInstance } = await this.common.patchJSON(url, values);
    return this.deserialize(apiInstance);
  }

  async remove(id: string): Promise<void> {
    const url = this.constructPath(`/${id}`);
    return await this.common.deleteJSON(url);
  }

  async upsertBasicAuth(id: string, basicAuth: string): Promise<void> {
    const url = this.constructPath(`/${id}/basic-auth`);
    await this.common.putJSON(url, { basicAuth });
  }

  async upsertBearerToken(id: string, bearerToken: string): Promise<void> {
    const url = this.constructPath(`/${id}/bearer-token`);
    await this.common.putJSON(url, { bearerToken });
  }

  async upsertClientSecret(id: string, clientSecret: string): Promise<void> {
    const url = this.constructPath(`/${id}/client-secret`);
    await this.common.putJSON(url, { clientSecret });
  }

  async upsertPrivateKey(id: string, privateKey: string): Promise<void> {
    const url = this.constructPath(`/${id}/private-key`);
    await this.common.putJSON(url, { privateKey });
  }
}
